import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import styled from "@emotion/styled";
import AutocompleteDropdown, {
  DropdownOption,
} from "../../../ui/molecules/AutocompleteDropdown";
import useDebounce from "../../../hooks/useDebounce";
import { Location } from "../types";
import LocationItem from "./LocationItem";
import { setSelectedGuest, setSelectedLocation } from "../store/checkoutSlice";
import { useNavigate } from "react-router-dom";
import { setError } from "../store/uiSlice";
import { useGetLocationsQuery } from "../services/managersApi";
import { skipToken } from "@reduxjs/toolkit/query";

const StyledH4 = styled.h4`
  color: #afabab;
  font-family: "Rethink-Sans-Bold", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

const StyledUl = styled.ul`
  padding-left: 10px;
`;

const StyledLi = styled.li`
  color: #afabab;
  font-family: "Rethink-Sans-Regular", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  position: relative;
  padding-left: 1.5em;
  list-style: none;
  &::before {
    content: "";
    position: absolute;
    left: 8px;
    top: 10px;
    width: 4px;
    height: 4px;
    background: #afabab;
    border-radius: 50%;
    display: inline-block;
  }

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px;
  margin-top: 40px;
`;

const LocationSelection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [locationQuery, setLocationQuery] = useState("");
  const debouncedLocationQuery = useDebounce(locationQuery, 225);
  const [locations, setLocations] = useState<Location[]>([]);

  const { data, error } = useGetLocationsQuery(
    debouncedLocationQuery.length >= 3 || !debouncedLocationQuery
      ? { type: "query", value: debouncedLocationQuery }
      : skipToken,
  );

  useEffect(() => {
    if (error) {
      dispatch(setError("Failed to fetch locations. Please try again."));
    } else {
      dispatch(setError(""));
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (data) {
      setLocations(data.locations);
    }
  }, [data]);

  useEffect(() => {
    dispatch(setSelectedGuest(null));
  }, []);

  const handleDropdownChange = (value: string) => {
    setLocationQuery(value);
  };

  const handleLocationSelect = (option: DropdownOption) => {
    dispatch(
      setSelectedLocation(
        locations.find((opt) => opt?.id === option.id) ?? null,
      ),
    );
    navigate("/diner-selection");
  };

  return (
    <div>
      <AutocompleteDropdown
        options={locations.map((location) => ({
          label: <LocationItem location={location} />,
          value: location.id.toString(),
          id: location.id,
        }))}
        onChange={handleLocationSelect}
        selectedOption={null}
        placeholder="Search for a location"
        onSearchChange={handleDropdownChange}
        isServerSide={true}
      />
      <FlexColumn>
        <StyledH4>Get started with your manager tools</StyledH4>
        <StyledUl>
          <StyledLi>
            <p>Search for a location to view activity or assist onsite staff</p>
          </StyledLi>
          <StyledLi>
            <p>Review recent transactions to track guest payments or issues</p>
          </StyledLi>
          <StyledLi>
            <p>
              Help a guest check out or issue a refund directly from their visit
              details
            </p>
          </StyledLi>
        </StyledUl>
      </FlexColumn>
    </div>
  );
};

export default LocationSelection;
